/**
 * Hand mirror of the backend's `src/features/am-tp-demo/contracts/` folder.
 *
 * The two repositories share no dependency, so this is a deliberate copy rather than an import — diff it
 * against those files whenever either side changes. The backend shapes them to make the copy mechanical:
 * cross-boundary discriminators are string-literal unions rather than TypeScript enums, nullable fields are
 * `T | null` rather than `T | undefined`, no `Date` or `bigint` crosses the wire, and money arrives as a
 * number in major units.
 *
 * Prisma enums are imported rather than copied, because this repository vendors its own generated client.
 */
import {
  Currency,
  TransferPricingPolicyMethod,
  TransferPricingTransactionTypeNew,
} from '@prisma/client';

/** Mirrors `am-tp-demo.task.types.ts`. */
export const AM_TP_DEMO_AI_TASKS = [
  'agreements.parse',
  'far.extract',
  'templates.parse',
  'workflow.assemble',
  'diff.summarize',
  'rollforward.evaluate',
  'erp.identify-intercompany',
  'dataroom.fetch',
] as const;

export type TAmDemoAiTask = (typeof AM_TP_DEMO_AI_TASKS)[number];

export const AM_TP_DEMO_ROUTES = {
  aiInvoke: 'demo/ai/invoke',
  clientOverview: 'demo/client-overview',
  finalizeFar: 'demo/far/finalize',
  saveTemplate: 'demo/templates/save',
  applyRollForward: 'demo/rollforward/apply',
  approveVersion: 'demo/versions/approve',
  confirmIntercompany: 'demo/erp/confirm-intercompany',
  exportMatrix: 'demo/matrix/export.xlsx',
  policyVersions: 'demo/agreements/versions',
  templateList: 'demo/templates/list',
  asset: 'demo/assets/document',
} as const;

/** Mirrors `am-tp-demo.entity.types.ts`. */
export type TAmDemoTargetRate = {
  percent: number;
  column: 'MARKUP' | 'PLI' | 'MARGIN';
};

export type TAmDemoPolicyDraft = {
  draftKey: string;
  sourceFileName: string;
  policyName: string;
  clientName: string;
  providerLegalName: string;
  providerCountry: string;
  counterpartyLegalName: string;
  counterpartyCountry: string;
  transactionType: TransferPricingTransactionTypeNew;
  method: TransferPricingPolicyMethod;
  /** As parsed from the source document. For the R&D agreement this is v1's rate, not the current one. */
  targetRate: TAmDemoTargetRate | null;
};

export type TAmDemoPlaceholderSourceType =
  | 'ENTITY_DATA'
  | 'FORM_INPUT'
  | 'FAR_ANALYSIS'
  | 'BENCHMARK_STUDY'
  | 'ERP_TRANSACTIONS';

export type TAmDemoPlaceholderGroup =
  | 'MAPPED'
  | 'NEEDS_CONFIRMATION'
  | 'COLLAPSED';

export type TAmDemoPlaceholderMapping = {
  placeholderKey: string;
  description: string;
  sourceType: TAmDemoPlaceholderSourceType;
  group: TAmDemoPlaceholderGroup;
  sourcePage: number;
};

export type TAmDemoWorkflowNodeKind =
  | 'START'
  | 'HUMAN_FORM'
  | 'LLM'
  | 'DOC_GEN'
  | 'HUMAN_REVIEW'
  | 'END';

export type TAmDemoFormFieldKind =
  | 'TEXT'
  | 'SELECT'
  | 'ENTITY_MULTI_SELECT'
  | 'MULTI_SELECT'
  | 'NUMBER'
  | 'FILE_UPLOAD';

export type TAmDemoFormField = {
  fieldKey: string;
  order: number;
  label: string;
  kind: TAmDemoFormFieldKind;
  seededValue: string | null;
  placeholder: string | null;
};

export type TAmDemoWorkflowNode = {
  nodeKey: string;
  order: number;
  kind: TAmDemoWorkflowNodeKind;
  label: string;
};

export type TAmDemoVersionRef = {
  versionId: string;
  versionNumber: number;
  authorName: string | null;
  createdAt: string;
  expiresAt: string;
  reviewState: 'PENDING_REVIEW' | 'APPROVED' | 'SUPERSEDED';
};

export type TAmDemoDiffSegment = {
  clauseNumber: string;
  clauseTitle: string;
  kind: 'CHANGED' | 'UNCHANGED';
  before: string;
  after: string;
};

/** One clause of an agreement, rendered against a single version. The document preview reads these in order. */
export type TAmDemoAgreementClause = {
  clauseNumber: string;
  clauseTitle: string;
  body: string;
};

/**
 * A version with everything the document preview and the download need.
 *
 * A superset of `TAmDemoVersionRef` rather than a second shape, so the version rail can render either without
 * knowing which endpoint produced it.
 */
export type TAmDemoAgreementVersion = TAmDemoVersionRef & {
  fileId: number;
  fileName: string;
  clauses: TAmDemoAgreementClause[];
};

/**
 * Every version of one policy's agreement.
 *
 * `policyId` is the real `TransferPricingPolicy` row, which is what joins this to the policies table; `policyKey`
 * is what the version-shaped tasks and the roll-forward take. Nothing else on the wire carries that mapping.
 */
export type TAmDemoPolicyVersions = {
  policyId: string;
  policyKey: string;
  currentVersionId: string | null;
  versions: TAmDemoAgreementVersion[];
  count: number;
};

/** A null `before` renders as an arrow with no left-hand side. */
export type TAmDemoChangeChip = {
  label: string;
  before: string | null;
  after: string;
};

export type TAmDemoSuggestionConfidence = 'HIGH' | 'LOW';

export type TAmDemoIntercompanySuggestion = {
  suggestionKey: string;
  glAccountCode: string;
  glAccountName: string;
  counterpartyLegalName: string;
  counterpartyCountry: string;
  amount: number;
  currency: string;
  suggestedClassification: string;
  confidence: TAmDemoSuggestionConfidence;
  isPreSelected: boolean;
};

export type TAmDemoFileDescriptor = {
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  /**
   * True when this document has real bytes behind it, which is what lets its chip be opened.
   *
   * Reported by the API rather than decided here: the frontend holds no file names of its own, and choosing which
   * chips open would mean writing the names of the documents that happen to exist into a frontend file.
   */
  hasAsset: boolean;
};

export type TAmDemoDataRoomProvider =
  | 'GOOGLE_DRIVE'
  | 'SHAREPOINT'
  | 'DROPBOX'
  | 'GENERIC_URL';

/** Mirrors `am-tp-demo.invoke.types.ts`. */
export type TAmDemoAgreementsParseResult = {
  chainOfThoughtSteps: string[];
  drafts: TAmDemoPolicyDraft[];
};

export type TAmDemoFarExtractResult = {
  subjectEntityLegalName: string;
  /** ISO-2, so the card's flag is derived from the same entity record the charts draw. */
  subjectEntityCountry: string;
  transcriptFileName: string;
  readiness: 'DRAFT_READY';
  functionsNarrative: string;
  assetsNarrative: string;
  risksNarrative: string;
  openGaps: string[];
};

export type TAmDemoTemplatesParseResult = {
  sourceFileName: string;
  pageCount: number;
  suggestedTemplateName: string;
  mappings: TAmDemoPlaceholderMapping[];
};

export type TAmDemoWorkflowAssembleResult = {
  workflowName: string;
  /**
   * The prompt §6.16 says was typed to produce this workflow, echoed back as the canvas's opening user turn.
   *
   * On the wire rather than restated here for the same reason `far.extract` carries `subjectEntityCountry`: the
   * text is a §6 value, so no frontend file may hold a copy of it.
   */
  prompt: string;
  status: 'DRAFT';
  nodes: TAmDemoWorkflowNode[];
  form: {
    formName: string;
    fields: TAmDemoFormField[];
  };
};

export type TAmDemoDiffSummarizeResult = {
  fromVersion: TAmDemoVersionRef;
  toVersion: TAmDemoVersionRef;
  segments: TAmDemoDiffSegment[];
  changes: TAmDemoChangeChip[];
};

export type TAmDemoRollforwardEvaluateResult = {
  verdict: 'ROLL_FORWARD_AS_IS' | 'GENERATE_NEW_AGREEMENT';
  recommendation: string;
  question: string;
  changeDescription: string | null;
  linkedFarGap: string | null;
  nextExpiry: string | null;
};

/**
 * The local disclosure the confirmed rows feed, named the way its own jurisdiction names it.
 *
 * On the payload rather than in a component because all three strings differ per jurisdiction — Germany's
 * Transaktionsmatrix and Australia's International Dealings Schedule reach the same screen — and a frontend
 * holding either set would be restating the spine.
 */
export type TAmDemoLocalRequirementRef = {
  name: string;
  annexLabel: string;
  /** The download's file-name stem; rebuilt here because a blob download cannot read `Content-Disposition`. */
  fileNameStem: string;
};

export type TAmDemoErpIdentifyResult = {
  erpProvider: string;
  syncFromDate: string;
  fiscalYear: number;
  scannedTransactionCount: number;
  suggestions: TAmDemoIntercompanySuggestion[];
  requirement: TAmDemoLocalRequirementRef;
};

export type TAmDemoDataroomFetchResult = {
  provider: TAmDemoDataRoomProvider;
  files: TAmDemoFileDescriptor[];
  /**
   * The functional-analysis interview transcript, kept apart from `files`.
   *
   * The room holds it alongside the nine agreements, but it belongs to a different beat: the first turn attaches
   * the agreements and the second attaches this. Returned as its own field rather than a tenth entry in `files`,
   * because `files` is what the intake card is derived against — appending to it would make the card claim ten
   * drafts.
   */
  farTranscriptFile: TAmDemoFileDescriptor;
};

/**
 * One entry per task, pairing its request context with its result.
 *
 * The envelopes below are mapped types over the full task union, so a task missing from this registry is a
 * compile error in both directions and each wrapper narrows from one generic rather than a hand-written union.
 */
export type TAmDemoAiTaskRegistry = {
  'agreements.parse': {
    context: { sourceFileNames: string[]; companyId?: number };
    result: TAmDemoAgreementsParseResult;
  };
  'far.extract': {
    context: { transcriptFileName: string; companyId?: number };
    result: TAmDemoFarExtractResult;
  };
  'templates.parse': {
    context: { sourceFileName: string; companyId?: number };
    result: TAmDemoTemplatesParseResult;
  };
  'workflow.assemble': {
    context: { prompt: string; companyId?: number };
    result: TAmDemoWorkflowAssembleResult;
  };
  'diff.summarize': {
    context: {
      policyKey: string;
      fromVersionNumber: number;
      toVersionNumber: number;
      companyId?: number;
    };
    result: TAmDemoDiffSummarizeResult;
  };
  'rollforward.evaluate': {
    context: {
      policyKey: string;
      hasFarChange: boolean;
      changeDescription: string | null;
      companyId?: number;
    };
    result: TAmDemoRollforwardEvaluateResult;
  };
  'erp.identify-intercompany': {
    context: { corporationId: number; fiscalYear: number; companyId?: number };
    result: TAmDemoErpIdentifyResult;
  };
  'dataroom.fetch': {
    context: { url: string; companyId?: number };
    result: TAmDemoDataroomFetchResult;
  };
};

export type TAmDemoAiContextFor<K extends TAmDemoAiTask> =
  TAmDemoAiTaskRegistry[K]['context'];

export type TAmDemoAiResultFor<K extends TAmDemoAiTask> =
  TAmDemoAiTaskRegistry[K]['result'];

export type TAmDemoAiInvokeResponseFor<K extends TAmDemoAiTask> = {
  task: K;
  /** A label the UI renders, not a duration — the endpoint answers in under four seconds regardless. */
  thinkingSeconds: number;
  result: TAmDemoAiResultFor<K>;
};

/**
 * Every demo agreement's version chain, for one client company.
 *
 * A read rather than a task: nothing here is inference. It exists because no other route lists versions —
 * `diff.summarize` returns the two ends of one comparison and the approval returns ids only on the run that
 * creates them — so the version chips, the rail and the document preview had no source after a page refresh.
 */
export type TAmDemoPolicyVersionsResponse = {
  policyVersions: TAmDemoPolicyVersions[];
  count: number;
};

/** One row of S13's template picker. `id` is a real `DocumentTemplate` where one exists, and the spine key otherwise. */
export type TAmDemoTemplateSummary = {
  id: string;
  name: string;
  versionLabel: string;
  isPublished: boolean;
};

/** The demo's own template library, in place of the product's unscoped, 2FA-guarded list. */
export type TAmDemoTemplateListResponse = {
  templates: TAmDemoTemplateSummary[];
  count: number;
};

export type TAmDemoClientOverviewResponse = {
  clientOverview: {
    /**
     * `YYYY-MM-DD` of the month the compliance calendar opens on, or null for a client with no compliance work.
     *
     * Derived from the client's own compliance items, so no frontend file holds a date.
     */
    calendarAnchorDate: string | null;
    totalEntities: number;
    activeJurisdictions: number;
    /** Policy agreements the client holds. Reads 1 before the agreements are approved and 5 after. */
    intercompanyRelationships: number;
    relatedPartyVolume: {
      amount: number;
      currency: string;
      fiscalYear: number;
    };
    /** The header's industry line. Null outside the demo roster — the header drops the segment. */
    industry: string | null;
    /**
     * The ERP the demo presents as connected, for the provider picker on the local-requirement step.
     *
     * Served rather than named in the frontend for the usual reason: it is a §6 value, and the same string has to
     * agree with the provider the ledger scan reports. Null off the demo roster.
     */
    connectedErpProvider: string | null;
  };
};

/** Mirrors `am-tp-demo.mutation.types.ts`. Requests carry keys, never values. */
export type TAmDemoApproveAgreementsRequest = {
  companyId: number;
  draftKeys: string[];
  overrides?: Record<
    string,
    {
      name?: string;
      transactionType?: TransferPricingTransactionTypeNew;
      method?: TransferPricingPolicyMethod;
      targetRatePercent?: number;
    }
  >;
};

export type TAmDemoApproveAgreementsResponse = {
  created: {
    draftKey: string;
    policyId: string;
    companyId: number;
    clientName: string;
    sourceFileId: number;
    /** The current version — v2 for the versioned agreement, v1 for the rest. This is what the chip renders. */
    versionId: string;
    versionIds: string[];
  }[];
  skipped: {
    draftKey: string;
    policyId: string;
    reason: 'ALREADY_EXISTS';
  }[];
};

export type TAmDemoFinalizeFarRequest = {
  corporationId: number;
  fiscalYear: number;
  sourceFileId?: number;
  narratives?: {
    functions: string;
    assets: string;
    risks: string;
  };
};

export type TAmDemoFinalizeFarResponse = {
  farAnalysisId: string;
  appliedToRecords: {
    tpDocumentRecordId: string;
    jurisdiction: string | null;
    layerKey: string;
    nowComplete: boolean;
  }[];
};

export type TAmDemoSaveTemplateRequest = {
  companyId: number;
  templateName: string;
  sourceFileName: string;
  confirmedPlaceholderKeys: string[];
  mappingOverrides?: Record<
    string,
    { sourceType?: TAmDemoPlaceholderSourceType }
  >;
};

export type TAmDemoSaveTemplateResponse = {
  documentTemplate: { id: string; versionId: string; name: string };
  mappingCount: number;
};

export type TAmDemoApplyRollForwardRequest = {
  companyId: number;
  policyKey: string;
  hasFarChange: boolean;
  changeDescription?: string;
};

export type TAmDemoApplyRollForwardResponse = {
  tpFileVersion: { id: string; versionNumber: number; expiresAt: string };
};

/** Approving the flagged version, which is what the redline screen's `Approve changes` does. */
export type TAmDemoApproveVersionRequest = {
  companyId: number;
  policyKey: string;
  versionId: string;
};

export type TAmDemoApproveVersionResponse = {
  tpFileVersion: {
    id: string;
    versionNumber: number;
    reviewState: 'APPROVED';
  };
};

export type TAmDemoConfirmIntercompanyRequest = {
  corporationId: number;
  fiscalYear: number;
  suggestionKeys: string[];
};

export type TAmDemoConfirmIntercompanyResponse = {
  confirmed: { suggestionKey: string; matrixRowId: string }[];
  skipped: { suggestionKey: string; reason: 'ALREADY_CONFIRMED' }[];
};

/** Mirrors `am-tp-demo.export.types.ts`. */
export type TAmDemoMatrixExportQuery = {
  corporationId: number;
  fiscalYear: number;
};

export type TAmDemoMatrixRow = {
  supplierEntityLegalName: string;
  supplierJurisdiction: string | null;
  recipientEntityLegalName: string;
  recipientJurisdiction: string | null;
  natureOfTransaction: TransferPricingTransactionTypeNew | null;
  amount: number;
  currency: Currency;
  contractualBasis: string | null;
  transferPricingMethod: TransferPricingPolicyMethod | null;
  isPreferentialRegime: boolean;
};

/** The shape every mutation in this folder returns, per the repository's coding standards. */
export type TAmDemoResult<TData> = {
  success: boolean;
  data?: TData;
  error?: string;
};
